/**
 * Usage (user menu → Users & roles → Usage, SUPERADMIN and TENANTADMIN only; its own page at
 * /usage until 2026-10-06): who uses the system, when and for how long, by user and by view, over
 * the last 7, 30 or 90 days.
 *
 * A tenant admin sees their own company's users; DistribuAI's own accounts (staff, demo, test)
 * are left out by the API. A superadmin may pick another client, or all, and show those accounts.
 * A row opens the user's views, recent visits and sign-ins. Period, users/views (`by`), client,
 * the staff switch and the open user live in the URL, so a view can be shared.
 *
 * Active time is what the tracker counts (src/app/activity.ts): the page in front and in use.
 */
import { useState } from 'react'
import type { ReactNode } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { ActionBadge, Button, DataTable, Modal, SegmentedControl } from '@/kit'
import type { Column, DataTableLabels, SortDirection } from '@/kit'
import './i18n'
import { absoluteTime, deviceLabel, formatDuration, relativeTime } from './format'
import { fetchUsage, fetchUsageUser, USAGE_PERIODS } from './usageApi'
import type { UsagePeriod, UsageQuery, UsageUser, UsageUserDetail, UsageView, UsageViewRef } from './usageApi'

type By = 'users' | 'views'
type Translate = (key: string, options?: Record<string, unknown>) => string

const PERIOD_DAYS: Record<UsagePeriod, number> = { '7d': 7, '30d': 30, '90d': 90 }
const DEFAULT_PERIOD: UsagePeriod = '30d'
const DASH = '–'

/** Rows sorted by one value; empty values always last. */
function sortRows<Row>(rows: Row[], dir: SortDirection, value: (row: Row) => number | string | null): Row[] {
    const sign = dir === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
        const va = value(a)
        const vb = value(b)
        if (va === vb) return 0
        if (va === null || va === '') return 1
        if (vb === null || vb === '') return -1
        return (typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb))) * sign
    })
}

const Muted = ({ children }: { children: ReactNode }) => <span className="text-[var(--text-weaker)]">{children}</span>

function When({ iso, now, locale, empty }: { iso: string | null; now: number; locale: string; empty: string }) {
    if (!iso) return <Muted>{empty}</Muted>
    return (
        <span title={absoluteTime(iso, locale)} className="tabular-nums">
            {relativeTime(iso, now, locale)}
        </span>
    )
}

function Count({ value }: { value: number }) {
    const { i18n } = useTranslation()
    return value > 0 ? <span className="tabular-nums">{value.toLocaleString(i18n.language)}</span> : <Muted>{DASH}</Muted>
}

function tableLabels(t: Translate, empty: string, loading: boolean): DataTableLabels {
    return {
        sortAscending: t('usage.sortAsc'),
        sortDescending: t('usage.sortDesc'),
        columnMenu: (header) => t('usage.columnMenu', { header }),
        empty: loading ? t('usage.loading') : empty,
        loading: t('usage.loading'),
    }
}

export function UsagePanel({ titleFor }: { titleFor: (path: string) => string | null }) {
    const { t, i18n } = useTranslation()
    const locale = i18n.language
    const [params, setParams] = useSearchParams()
    const period = (USAGE_PERIODS as readonly string[]).includes(params.get('period') ?? '') ? (params.get('period') as UsagePeriod) : DEFAULT_PERIOD
    const by: By = params.get('by') === 'views' ? 'views' : 'users'
    const tenant = params.get('tenant') ?? undefined
    const staff = params.get('staff') === '1'
    const openUser = Number(params.get('user')) || null

    function update(changes: Record<string, string | null>) {
        const next = new URLSearchParams(params)
        for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === '') next.delete(key)
            else next.set(key, value)
        }
        setParams(next, { replace: true })
    }

    const query: UsageQuery = { period, tenant, staff }
    const report = useQuery({
        queryKey: ['usage', query],
        queryFn: ({ signal }) => fetchUsage(query, signal),
        placeholderData: keepPreviousData,
    })
    const data = report.data
    // "3 days ago" is measured from when the figures were fetched.
    const now = report.dataUpdatedAt

    // The platform's own pages by name: Users & roles, and the usage page's old address.
    const own: Record<string, string> = { '/team': t('team.title'), '/usage': t('usage.title') }
    const viewName = (ref: UsageViewRef) => own[ref.page_path.split('?')[0] ?? ''] ?? titleFor(ref.page_path) ?? ref.page_title ?? ref.page_path

    const [userSort, setUserSort] = useState<{ key: string; dir: SortDirection }>({ key: 'active', dir: 'desc' })
    const [viewSort, setViewSort] = useState<{ key: string; dir: SortDirection }>({ key: 'active', dir: 'desc' })

    const userValue = (u: UsageUser): number | string | null => {
        switch (userSort.key) {
            case 'user': return u.name || u.email
            case 'lastSeen': return u.last_seen
            case 'lastLogin': return u.last_login
            case 'logins': return u.logins
            case 'days': return u.active_days
            case 'visits': return u.visits
            default: return u.active_seconds
        }
    }
    const users = sortRows(data?.users ?? [], userSort.dir, userValue)

    const viewValue = (v: UsageView): number | string | null => {
        switch (viewSort.key) {
            case 'view': return viewName(v)
            case 'users': return v.users
            case 'visits': return v.visits
            case 'avg': return v.visits > 0 ? v.active_seconds / v.visits : 0
            case 'lastUsed': return v.last_visited_at
            default: return v.active_seconds
        }
    }
    const views = sortRows(data?.views ?? [], viewSort.dir, viewValue)

    const roleLabel = (roles: string[]) => roles.map((r) => t(`usage.role.${r}`, { defaultValue: r })).join(', ')

    const userColumns: Column<UsageUser>[] = [
        {
            key: 'user',
            header: t('usage.col.user'),
            width: 'fill',
            sticky: true,
            sortable: true,
            render: (u) => (
                <span className="flex min-w-0 items-center gap-[6px]" title={u.email}>
                    <span className="truncate font-[550]">{u.name || u.email}</span>
                    {u.staff && <ActionBadge tone="neutral">{t('usage.staffBadge')}</ActionBadge>}
                    {!u.enabled && <ActionBadge tone="warning">{t('usage.disabled')}</ActionBadge>}
                </span>
            ),
        },
        { key: 'email', header: t('usage.col.email'), width: 220, render: (u) => <span className="block truncate text-[var(--text-weak)]" title={u.email}>{u.email}</span> },
        { key: 'role', header: t('usage.col.role'), width: 130, render: (u) => roleLabel(u.roles) },
        { key: 'lastSeen', header: t('usage.col.lastSeen'), width: 150, sortable: true, render: (u) => <When iso={u.last_seen} now={now} locale={locale} empty={t('usage.never')} /> },
        { key: 'lastLogin', header: t('usage.col.lastLogin'), width: 170, sortable: true, render: (u) => <When iso={u.last_login} now={now} locale={locale} empty={t('usage.never')} /> },
        { key: 'logins', header: t('usage.col.logins'), width: 120, sortable: true, render: (u) => <Count value={u.logins} /> },
        { key: 'days', header: t('usage.col.activeDays'), width: 130, sortable: true, render: (u) => <Count value={u.active_days} /> },
        {
            key: 'active',
            header: t('usage.col.activeTime'),
            width: 130,
            sortable: true,
            render: (u) => (u.active_seconds > 0 ? <span className="tabular-nums">{formatDuration(u.active_seconds, t)}</span> : <Muted>{DASH}</Muted>),
        },
        { key: 'visits', header: t('usage.col.visits'), width: 100, sortable: true, render: (u) => <Count value={u.visits} /> },
        { key: 'top', header: t('usage.col.topView'), width: 200, render: (u) => (u.top_view ? <span title={u.top_view.page_path}>{viewName(u.top_view)}</span> : <Muted>{DASH}</Muted>) },
    ]

    const viewColumns: Column<UsageView>[] = [
        {
            key: 'view',
            header: t('usage.col.view'),
            width: 'fill',
            sticky: true,
            sortable: true,
            render: (v) => (
                <span className="flex min-w-0 items-baseline gap-[8px]" title={v.page_path}>
                    <span className="truncate font-[550]">{viewName(v)}</span>
                    <span className="truncate text-[12px] leading-[16px] text-[var(--text-weak)]">{v.page_path}</span>
                </span>
            ),
        },
        { key: 'users', header: t('usage.col.users'), width: 110, sortable: true, render: (v) => <Count value={v.users} /> },
        { key: 'visits', header: t('usage.col.visits'), width: 110, sortable: true, render: (v) => <Count value={v.visits} /> },
        { key: 'active', header: t('usage.col.activeTime'), width: 140, sortable: true, render: (v) => <span className="tabular-nums">{formatDuration(v.active_seconds, t)}</span> },
        { key: 'avg', header: t('usage.col.avgVisit'), width: 130, sortable: true, render: (v) => <span className="tabular-nums">{formatDuration(v.visits > 0 ? v.active_seconds / v.visits : 0, t)}</span> },
        { key: 'lastUsed', header: t('usage.col.lastUsed'), width: 170, sortable: true, render: (v) => <When iso={v.last_visited_at} now={now} locale={locale} empty={DASH} /> },
    ]

    const scope = data?.scope
    const totals = data?.totals
    const selectedUser = openUser ? data?.users.find((u) => u.id === openUser) ?? null : null

    return (
        <div className="flex w-full flex-col">
            <div className="flex w-full flex-wrap items-center gap-[16px] gap-y-[12px] px-[var(--layout-page-padding-x)] py-[16px]">
                <div className="flex max-w-full min-w-0">
                    <SegmentedControl<By>
                        ariaLabel={t('usage.tabs')}
                        value={by}
                        onChange={(next) => update({ by: next === 'users' ? null : next, user: null })}
                        options={[
                            { value: 'users', label: t('usage.tab.users') },
                            { value: 'views', label: t('usage.tab.views') },
                        ]}
                    />
                </div>
                <div className="flex max-w-full min-w-0">
                    <SegmentedControl<UsagePeriod>
                        ariaLabel={t('usage.period')}
                        value={period}
                        onChange={(v) => update({ period: v === DEFAULT_PERIOD ? null : v })}
                        options={USAGE_PERIODS.map((p) => ({ value: p, label: t('usage.days', { count: PERIOD_DAYS[p] }) }))}
                    />
                </div>
                {scope?.can_choose_tenant && data?.tenants && (
                    <label className="flex items-center gap-[8px] text-[14px] leading-[20px] text-[var(--text-weak)]">
                        {t('usage.tenant')}
                        {/* Uncontrolled, remounted when the URL changes: a controlled one snaps back to the
                            old value until the router's (deferred) navigation lands. */}
                        <select
                            key={tenant ?? 'session'}
                            defaultValue={tenant ?? (scope.tenant_id === null ? 'all' : String(scope.tenant_id))}
                            onChange={(event) => update({ tenant: event.target.value, user: null })}
                            className="h-[36px] cursor-pointer rounded-[6px] border border-solid border-[var(--border-default)] bg-[var(--surface-default)] px-[8px] text-[14px] leading-[20px] text-[var(--text-default)] focus-visible:shadow-[var(--focus-m)] focus-visible:outline-none"
                        >
                            <option value="all">{t('usage.allTenants')}</option>
                            {data.tenants.map((option) => (
                                <option key={option.id} value={String(option.id)}>
                                    {option.name}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
                {scope?.can_include_staff && (
                    <label className="flex cursor-pointer items-center gap-[8px] text-[14px] leading-[20px] text-[var(--text-default)]">
                        <input
                            key={String(staff)}
                            type="checkbox"
                            defaultChecked={staff}
                            onChange={(event) => update({ staff: event.target.checked ? '1' : null, user: null })}
                            className="size-[16px] cursor-pointer accent-[var(--text-default)]"
                        />
                        {t('usage.staff')}
                    </label>
                )}
                <p className="m-0 text-[14px] leading-[20px] font-normal text-[var(--text-weak)]">{t('usage.hint')}</p>
            </div>

            {report.isError && !data && (
                <div className="flex w-full flex-wrap items-center gap-[16px] gap-y-[8px] px-[var(--layout-page-padding-x)] pb-[16px]">
                    <p role="alert" className="m-0 text-[14px] leading-[20px] font-normal text-[var(--text-danger)]">
                        {t('usage.loadFailed')}
                    </p>
                    <Button variant="secondary" onClick={() => void report.refetch()}>
                        {t('usage.retry')}
                    </Button>
                </div>
            )}

            <section aria-label={t('usage.title')} aria-busy={report.isPending || undefined} className="w-full px-[var(--layout-page-padding-x)] pb-[16px]">
                <ul className="m-0 grid list-none grid-cols-2 gap-[12px] p-0 max-sm:gap-[8px] lg:grid-cols-4">
                    <Kpi label={t('usage.kpi.activeUsers')} value={totals ? totals.active_users.toLocaleString(locale) : DASH} sub={totals ? t('usage.kpi.ofUsers', { total: totals.users }) : ''} />
                    <Kpi label={t('usage.kpi.activeTime')} value={totals ? formatDuration(totals.active_seconds, t) : DASH} />
                    <Kpi label={t('usage.kpi.visits')} value={totals ? totals.visits.toLocaleString(locale) : DASH} />
                    <Kpi label={t('usage.kpi.logins')} value={totals ? totals.logins.toLocaleString(locale) : DASH} />
                </ul>
            </section>

            <div className={`w-full transition-opacity duration-200 ${report.isFetching && data ? 'opacity-60' : ''}`}>
                {by === 'users' ? (
                    <DataTable
                        columns={userColumns}
                        rows={users}
                        rowKey={(u) => String(u.id)}
                        loading={report.isPending}
                        onSort={(key, dir) => setUserSort({ key, dir })}
                        onRowClick={(u) => update({ user: String(u.id) })}
                        labels={tableLabels(t, t('usage.empty.users'), report.isPending)}
                        fillMinWidth={240}
                    />
                ) : (
                    <DataTable
                        columns={viewColumns}
                        rows={views}
                        rowKey={(v) => v.page_path}
                        loading={report.isPending}
                        onSort={(key, dir) => setViewSort({ key, dir })}
                        labels={tableLabels(t, t('usage.empty.views'), report.isPending)}
                        fillMinWidth={240}
                    />
                )}
            </div>

            {openUser !== null && (
                <UserDetail
                    userId={openUser}
                    user={selectedUser}
                    query={query}
                    viewName={viewName}
                    now={now}
                    locale={locale}
                    onClose={() => update({ user: null })}
                />
            )}
        </div>
    )
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
    return (
        <li className="flex min-w-0 flex-col gap-[4px] rounded-[8px] border border-solid border-[var(--border-default)] bg-[var(--surface-default)] px-[16px] py-[12px] max-sm:px-[12px]">
            <p className="m-0 truncate text-[14px] leading-[20px] font-normal text-[var(--text-weak)]">{label}</p>
            <p className="m-0 flex items-baseline gap-[6px] whitespace-nowrap">
                <span className="text-[22px] leading-[28px] font-[550] text-[var(--text-default)] tabular-nums">{value}</span>
                {sub && <span className="text-[14px] leading-[20px] font-normal text-[var(--text-weak)]">{sub}</span>}
            </p>
        </li>
    )
}

function UserDetail({
    userId,
    user,
    query,
    viewName,
    now,
    locale,
    onClose,
}: {
    userId: number
    user: UsageUser | null
    query: UsageQuery
    viewName: (ref: UsageViewRef) => string
    now: number
    locale: string
    onClose: () => void
}) {
    const { t } = useTranslation()
    const detail = useQuery({
        queryKey: ['usage', 'user', userId, query],
        queryFn: ({ signal }) => fetchUsageUser(query, userId, signal),
    })
    const d: UsageUserDetail | undefined = detail.data
    const loading = detail.isPending

    const viewColumns: Column<UsageView>[] = [
        { key: 'view', header: t('usage.col.view'), width: 'fill', render: (v) => <span title={v.page_path}>{viewName(v)}</span> },
        { key: 'visits', header: t('usage.col.visits'), width: 100, render: (v) => <Count value={v.visits} /> },
        { key: 'active', header: t('usage.col.activeTime'), width: 130, render: (v) => <span className="tabular-nums">{formatDuration(v.active_seconds, t)}</span> },
        { key: 'lastUsed', header: t('usage.col.lastUsed'), width: 150, render: (v) => <When iso={v.last_visited_at} now={now} locale={locale} empty={DASH} /> },
    ]
    type Visit = UsageUserDetail['recent_visits'][number]
    const visitColumns: Column<Visit>[] = [
        { key: 'when', header: t('usage.col.when'), width: 190, render: (v) => <span className="tabular-nums">{absoluteTime(v.visited_at, locale)}</span> },
        { key: 'view', header: t('usage.col.view'), width: 'fill', render: (v) => <span title={v.page_path}>{viewName(v)}</span> },
        { key: 'duration', header: t('usage.col.duration'), width: 130, render: (v) => <span className="tabular-nums">{formatDuration(v.duration_seconds, t)}</span> },
    ]
    type Login = UsageUserDetail['recent_logins'][number]
    const loginColumns: Column<Login>[] = [
        { key: 'when', header: t('usage.col.when'), width: 220, render: (l) => <span className="tabular-nums">{absoluteTime(l.at, locale)}</span> },
        { key: 'device', header: t('usage.col.device'), width: 'fill', render: (l) => <span title={l.user_agent ?? undefined}>{deviceLabel(l.user_agent) || DASH}</span> },
    ]
    const empty = t('usage.detail.empty')

    return (
        <Modal
            open
            onClose={onClose}
            size="xl"
            title={user ? user.name || user.email : t('usage.col.user')}
            closeLabel={t('usage.close')}
            subtitle={
                user ? (
                    <span className="text-[14px] leading-[20px] text-[var(--text-weak)]">
                        {user.email} · {t('usage.detail.summary', { logins: user.logins, days: user.active_days, time: formatDuration(user.active_seconds, t) })}
                    </span>
                ) : undefined
            }
        >
            <div className="flex flex-col gap-[24px]">
                {detail.isError && (
                    <p role="alert" className="m-0 text-[14px] leading-[20px] text-[var(--text-danger)]">
                        {t('usage.loadFailed')}
                    </p>
                )}
                <DetailSection title={t('usage.detail.views')}>
                    <DataTable columns={viewColumns} rows={d?.views ?? []} rowKey={(v) => v.page_path} loading={loading} labels={tableLabels(t, empty, loading)} maxHeight="280px" />
                </DetailSection>
                <DetailSection title={t('usage.detail.recentVisits')}>
                    <DataTable
                        columns={visitColumns}
                        rows={d?.recent_visits ?? []}
                        rowKey={(v) => `${v.visited_at}-${v.page_path}`}
                        loading={loading}
                        labels={tableLabels(t, empty, loading)}
                        maxHeight="280px"
                    />
                </DetailSection>
                <DetailSection title={t('usage.detail.recentLogins')}>
                    <DataTable columns={loginColumns} rows={d?.recent_logins ?? []} rowKey={(l) => String(l.at)} loading={loading} labels={tableLabels(t, empty, loading)} maxHeight="240px" />
                </DetailSection>
            </div>
        </Modal>
    )
}

function DetailSection({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section className="flex flex-col gap-[8px]">
            <h2 className="m-0 text-[16px] leading-[24px] font-[550] text-[var(--text-default)]">{title}</h2>
            {children}
        </section>
    )
}
